import { useEffect, useState } from 'react';
import { Download, X, ZoomIn, ZoomOut } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export type MediaViewerProps = {
  url: string;
  name?: string | null;
  type?: string | null;
  open: boolean;
  onClose: () => void;
};

const isImageType = (type?: string | null) => (type ?? '').startsWith('image/');
const isPdfType = (type?: string | null) => (type ?? '') === 'application/pdf';

export const MediaViewer = ({ url, name, type, open, onClose }: MediaViewerProps) => {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!open) {
      setZoom(1);
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(z + 0.25, 4));
      if (e.key === '-') setZoom((z) => Math.max(z - 0.25, 0.5));
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const image = isImageType(type);
  const pdf = isPdfType(type);
  const displayName = name ?? 'Arquivo';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Visualizando ${displayName}`}
      className="fixed inset-0 z-[100] flex flex-col bg-black/95"
      onClick={(e) => {
        // Click no backdrop fecha. Click no conteudo nao.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <header className="flex items-center justify-between gap-3 px-4 py-3 text-white">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{displayName}</p>
          {type ? <p className="truncate text-xs text-white/60">{type}</p> : null}
        </div>
        <div className="flex items-center gap-1">
          {image ? (
            <>
              <button
                type="button"
                aria-label="Reduzir zoom"
                onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white transition hover:bg-white/10"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Aumentar zoom"
                onClick={() => setZoom((z) => Math.min(z + 0.25, 4))}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white transition hover:bg-white/10"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
            </>
          ) : null}
          <a
            href={url}
            download={displayName}
            aria-label="Baixar arquivo"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white transition hover:bg-white/10"
          >
            <Download className="h-4 w-4" />
          </a>
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white transition hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </header>

      <div
        className={cn(
          'flex flex-1 items-center justify-center overflow-auto px-4 pb-4',
          image && 'cursor-zoom-in',
        )}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {image ? (
          <img
            src={url}
            alt={displayName}
            style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
            className="max-h-full max-w-full select-none object-contain transition-transform"
            draggable={false}
            onDoubleClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
          />
        ) : pdf ? (
          // sandbox sem allow-scripts: PDF renderiza, mas qualquer JS embutido
          // nao executa. Mantem visualizacao segura mesmo se o anexo for
          // adulterado (defesa em camadas alem da validacao mime no upload).
          <iframe
            src={url}
            title={displayName}
            sandbox=""
            className="h-full max-h-[85vh] w-full max-w-4xl rounded-lg border border-white/10 bg-white"
          />
        ) : (
          <div className="flex flex-col items-center gap-3 text-white">
            <p className="text-sm text-white/80">Pré-visualização não disponível para este tipo.</p>
            <a
              href={url}
              download={displayName}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-white px-4 text-sm font-semibold text-black transition hover:bg-white/90"
            >
              <Download className="h-4 w-4" /> Baixar
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
